/**
 * Fetch-based EventSource polyfill.
 *
 * The native `EventSource` API cannot set custom request headers, which
 * blocks our use of `Last-Event-ID` on manual reconnects (the browser
 * only includes it automatically across its own internal retries — which
 * we replace with a richer back-off + heartbeat strategy in `useSSE`).
 *
 * This polyfill:
 *   - opens the stream via `fetch()` so we can attach arbitrary headers
 *   - parses the `text/event-stream` wire format incrementally
 *   - dispatches `MessageEvent`-shaped objects to listeners, matching the
 *     subset of the EventSource API that `useSSE` actually uses
 *   - supports `AbortController`-based cancellation
 *   - is environment-portable: works in browsers, jsdom, and node 18+
 *     (via global `fetch`)
 *
 * Wire vocabulary handled per WHATWG `text/event-stream`:
 *   - `event:` sets the dispatched event name (default `"message"`)
 *   - `data:` accumulates with `\n` separators
 *   - `id:` updates the polyfill's `lastEventId` cursor
 *   - `retry:` updates the suggested retry delay
 *   - lines beginning with `:` are comments (used for heartbeats)
 *   - blank line dispatches the buffered event
 */

export interface FetchEventSourceInit {
  /** Full URL including any query string (locale, slug, etc.). */
  url: string;
  /** Optional last seen event id; sent as `Last-Event-ID` header. */
  lastEventId?: string;
  /** Optional `AbortSignal` to cancel the underlying fetch. */
  signal?: AbortSignal;
  /** When `true`, sends cookies. Matches `EventSource.withCredentials`. */
  withCredentials?: boolean;
  /** Custom `fetch` implementation; defaults to global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Invoked when the response opens with status 200. */
  onOpen?: () => void;
  /** Invoked for each fully-formed event (including comment heartbeats). */
  onEvent?: (event: PolyfillEvent) => void;
  /** Invoked when the stream terminates (clean close or error). */
  onClose?: (err: Error | null) => void;
  /** Invoked on any byte received — used by the heartbeat watchdog. */
  onChunk?: () => void;
}

export interface PolyfillEvent {
  /** SSE `event:` name, or `"message"` when omitted. */
  type: string;
  /** Concatenated `data:` payload (newline-joined when multi-line). */
  data: string;
  /** Last `id:` cursor seen on the stream (cumulative). */
  lastEventId: string;
  /** True for comment lines (`: ping …`) — used to refresh the watchdog. */
  isComment: boolean;
}

const NEWLINE_REGEX = /\r\n|\r|\n/;

export interface FetchEventSourceHandle {
  /** Cancel the stream and release resources. */
  close(): void;
  /** The cursor the polyfill last saw on the wire. */
  readonly lastEventId: string;
}

export function openFetchEventSource(
  init: FetchEventSourceInit,
): FetchEventSourceHandle {
  const fetchImpl = init.fetchImpl ?? fetch;
  const controller = new AbortController();
  // If the caller passes a signal, mirror its abort into our controller.
  if (init.signal) {
    if (init.signal.aborted) controller.abort();
    else init.signal.addEventListener("abort", () => controller.abort());
  }
  let closed = false;
  let cursor = init.lastEventId ?? "";

  const headers: Record<string, string> = {
    Accept: "text/event-stream",
    "Cache-Control": "no-cache",
  };
  if (cursor) headers["Last-Event-ID"] = cursor;

  const run = async () => {
    try {
      const response = await fetchImpl(init.url, {
        method: "GET",
        headers,
        credentials: init.withCredentials ? "include" : "same-origin",
        signal: controller.signal,
      });

      if (closed) return;

      if (!response.ok) {
        throw new Error(`sse_http_${response.status}`);
      }
      if (!response.body) {
        throw new Error("sse_no_body");
      }

      init.onOpen?.();

      const reader = response.body
        .pipeThrough(new TextDecoderStream())
        .getReader();

      let buffer = "";
      let eventName = "";
      let dataLines: string[] = [];
      let commentLines: string[] = [];

      const dispatch = () => {
        if (commentLines.length > 0) {
          init.onEvent?.({
            type: "comment",
            data: commentLines.join("\n"),
            lastEventId: cursor,
            isComment: true,
          });
          commentLines = [];
        }
        if (dataLines.length === 0) {
          eventName = "";
          return;
        }
        init.onEvent?.({
          type: eventName || "message",
          data: dataLines.join("\n"),
          lastEventId: cursor,
          isComment: false,
        });
        eventName = "";
        dataLines = [];
      };

      while (!closed) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) {
          init.onChunk?.();
          buffer += value;
          // Each iteration consumes complete lines; keep the trailing
          // partial line in `buffer` for the next read.
          while (true) {
            const match = NEWLINE_REGEX.exec(buffer);
            if (!match) break;
            const idx = match.index;
            const line = buffer.slice(0, idx);
            buffer = buffer.slice(idx + match[0].length);

            if (line === "") {
              // Blank line ⇒ dispatch the accumulated event.
              dispatch();
              continue;
            }
            if (line.startsWith(":")) {
              commentLines.push(line.slice(1).trimStart());
              continue;
            }
            const colonIdx = line.indexOf(":");
            const field = colonIdx === -1 ? line : line.slice(0, colonIdx);
            // Per spec: if the value starts with a space, strip exactly one.
            let value = colonIdx === -1 ? "" : line.slice(colonIdx + 1);
            if (value.startsWith(" ")) value = value.slice(1);

            switch (field) {
              case "event":
                eventName = value;
                break;
              case "data":
                dataLines.push(value);
                break;
              case "id":
                // The spec forbids NULs in the id; ignore the field if present.
                if (!value.includes(" ")) cursor = value;
                break;
              case "retry":
                // `useSSE` owns its retry schedule; we accept and ignore.
                break;
              default:
                // Unknown field — silently ignored per spec.
                break;
            }
          }
        }
      }
      init.onClose?.(null);
    } catch (err) {
      if (closed) {
        init.onClose?.(null);
        return;
      }
      init.onClose?.(err instanceof Error ? err : new Error(String(err)));
    }
  };

  // Fire-and-forget; `close()` aborts via the controller.
  void run();

  return {
    close() {
      if (closed) return;
      closed = true;
      controller.abort();
    },
    get lastEventId() {
      return cursor;
    },
  };
}
